import { embryoStageColor, embryoStageLabel, isFrozen, storageLabel } from '../../../core/constants/transfers';
import { DX_RESULTS, DX_RESULT_BY_VALUE, PREG_STATE } from '../../../core/constants/dx';

// CL legible (dónde palpar) desde remarks.corpusLuteum = { corpusLuteum:{left,right}, quality, cavernous }.
function fmtCL(cl) {
    if (!cl) return null;
    const parts = [];
    if (cl.corpusLuteum?.left) parts.push('Izq');
    if (cl.corpusLuteum?.right) parts.push('Der');
    if (cl.quality) parts.push(`C${cl.quality}`);
    if (cl.cavernous) parts.push('cav');
    return parts.length ? parts.join(' · ') : null;
}

function ResultChip({ number, result }) {
    const r = DX_RESULT_BY_VALUE[result];
    return <span className={`dx-hist dx-hist--${r?.tone || 'warn'}`} title={r?.label || result}>dx{number}: {r?.short || result}</span>;
}

// Grilla de Dx por FASE (dx1 o dx2): una preñez por fila. Embrión por GENEALOGÍA
// (madre × padre), CL para saber dónde palpar. El selector apunta al dx de la fase
// (`info`): registrar (vacío), editar (precargado con el resultado actual, 0031) o
// solo lectura si quedó congelado por un dx posterior. Los dx de otras fases se
// muestran como contexto. Aviso por fila si se registra antes de la fecha estimada.
export default function DxRows({ rows, phase, results, onResult, canWrite = true, saving = false }) {
    return (
        <div className="dx-table-wrap">
            <table className="dx-table">
                <thead>
                    <tr>
                        <th className="dx-th-num">#</th>
                        <th>Receptora</th>
                        <th>Genealogía</th>
                        <th>CL</th>
                        <th>Finca</th>
                        <th>Estado</th>
                        <th className="dx-th-result">Diagnóstico · dx{phase}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((p, i) => {
                        const st = PREG_STATE[p.state] || { label: p.state, tone: 'open' };
                        const info = p.info || { mode: 'na' };
                        const picked = results[p.id];
                        const selected = picked != null ? picked : (info.mode === 'edit' ? info.current : null);
                        const l = p.embryo?.lineage || {};
                        const donor = l.donor?.name;
                        const sire = l.sire?.name;
                        const gen = (donor || sire) ? `${donor || '—'} × ${sire || '—'}` : null;
                        const cl = fmtCL(p.cl);
                        // contexto: dx ya registrados de OTRAS fases (orientación).
                        const context = (p.diagnostics || []).filter((d) => d.result != null && d.number !== phase);
                        return (
                            <tr key={p.id} className="dx-tr" style={{ '--session': p.color }}>
                                <td className="dx-td-num">{i + 1}</td>
                                <td className="dx-td-recipient">
                                    <span className="dx-recipient">{p.recipient?.number || '—'}</span>
                                    {p.recipient?.name && <span className="dx-recipient__sub">{p.recipient.name}</span>}
                                </td>
                                <td className="dx-td-gen">
                                    <span className="dx-gen">{gen || '—'}</span>
                                    {p.embryo?.stage && <span className="dx-stage" style={{ '--stage': embryoStageColor(p.embryo.stage) }}>{embryoStageLabel(p.embryo.stage)}</span>}
                                    {isFrozen(p.embryo?.storage) && <span className="dx-storage dx-storage--frozen" title="Embrión transferido congelado">{storageLabel(p.embryo.storage)}</span>}
                                </td>
                                <td className="dx-td-cl">{cl ? <span className="dx-cl">{cl}</span> : <span className="dx-cl--none">—</span>}</td>
                                <td className="dx-td-farm">{p.recipient_farm?.name || '—'}</td>
                                <td><span className={`dx-state dx-state--${st.tone}`}>{st.label}</span></td>
                                <td className="dx-td-result">
                                    {context.length > 0 && (
                                        <div className="dx-history">
                                            {context.map((d) => <ResultChip key={d.number} number={d.number} result={d.result} />)}
                                        </div>
                                    )}

                                    {!p.detailLoaded ? (
                                        <span className="dx-terminal">Cargando…</span>
                                    ) : info.mode === 'locked' ? (
                                        <div className="dx-next">
                                            <span className="dx-next__label">dx{phase}</span>
                                            <ResultChip number={phase} result={info.current} />
                                            <span className="dx-locked" title="Hay un dx posterior registrado">posterior registrado</span>
                                        </div>
                                    ) : (
                                        <div className="dx-next">
                                            <span className="dx-next__label">
                                                dx{phase}
                                                {info.mode === 'edit' ? ' · editar' : (info.kind === 'extemporaneous' ? ' · extemp.' : '')}
                                            </span>
                                            <div className="dx-results" role="group" aria-label={`Resultado dx${phase}`}>
                                                {DX_RESULTS.map((r) => (
                                                    <button
                                                        key={r.value}
                                                        type="button"
                                                        className={`dx-result-btn dx-result-btn--${r.tone} ${selected === r.value ? 'dx-result-btn--active' : ''}`}
                                                        onClick={() => onResult(p.id, picked === r.value ? null : r.value)}
                                                        title={r.label}
                                                        disabled={!canWrite || saving}
                                                    >
                                                        {r.short}
                                                    </button>
                                                ))}
                                            </div>
                                            {info.earlyDays > 0 && (
                                                <span className="dx-early" title={`Fecha estimada de dx${phase}: faltan ${info.earlyDays} día(s)`}>
                                                    faltan {info.earlyDays} d
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
